import { Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { environment } from "src/environments/environment";
import { ApiService } from "src/app/api.service";
import { LoginMultiModuloService } from "../login-multi-modulo/login-multi-modulo.service";
import { PerformanceModuleApiService } from "./performance-module-api.service";

const CUSTOM_CONFIG_KEY = "loggedCustomConfig";
/** Dentro de este plazo una entrada repetida (el arranque y luego el guard) no vuelve a pedir. */
const MIN_INTERVAL_MS = 10_000;
const COLOR_KEYS = ["color_primario_app", "color_secundario_app", "color_terciario_app"] as const;

/**
 * Toma los colores de Personalización → Aplicación sin volver a iniciar sesión.
 * Hoy solo se aplican en el login (`custom_ambiente`); al entrar al módulo de
 * Desempeño se piden otra vez y se aplican por la MISMA vía del login
 * (`handleThemeMergeAndApplyFinalTheme`), con la base que corresponde a la marca.
 *
 * Nunca interrumpe: si la llamada falla (red, 403 de `workspace.access`…) el tema
 * actual se queda, y si los colores no cambiaron no se vuelve a aplicar, para no
 * repintar la pantalla.
 */
@Injectable({ providedIn: "root" })
export class PerformanceThemeRefreshService {
	constructor(
		private performanceApi: PerformanceModuleApiService,
		private loginMultiModuloService: LoginMultiModuloService,
		private api: ApiService,
	) {}

	private inFlight: Promise<void> | null = null;
	private lastRequestAt = 0;

	/**
	 * Al arrancar la app con la sesión de Desempeño ya abierta (recarga o regreso a la
	 * pestaña): pide los colores sin esperar a que el guard de la ruta termine de cargar
	 * el módulo, que es lo que retrasaba el cambio unos cientos de milisegundos. Solo
	 * actúa si ya hay token de Desempeño; el handshake sigue siendo del guard.
	 */
	async refreshOnBoot(pathname: string = window.location.pathname): Promise<void> {
		if (!pathname.startsWith("/performance") || !localStorage.getItem("performance_token")) return;
		await this.refresh();
	}

	/** Una sola petición a la vez: el arranque y el guard pueden coincidir. */
	refresh(): Promise<void> {
		if (this.inFlight) return this.inFlight;
		if (Date.now() - this.lastRequestAt < MIN_INTERVAL_MS) return Promise.resolve();
		this.inFlight = this.doRefresh().finally(() => (this.inFlight = null));
		return this.inFlight;
	}

	private async doRefresh(): Promise<void> {
		// Al arrancar, el usuario puede no estar cargado todavía: sin sus slugs la URL
		// saldría rota. El guard lo reintenta cuando ya está.
		if (!this.performanceApi.workspaceSlug || !this.performanceApi.subworkspaceSlug) return;

		this.lastRequestAt = Date.now();
		try {
			const response = await firstValueFrom(this.performanceApi.getSettings());
			const theme = response?.data?.app_theme;
			if (!theme) return;

			const stored = this.readStoredCustomConfig();
			const active = !!theme.custom_workspace_active && !!theme.color_primario_app;

			if (!active) {
				// Sin personalización activa se vuelve al tema base de la marca.
				if (!stored) return;
				localStorage.removeItem(CUSTOM_CONFIG_KEY);
				await this.apply(null);
				return;
			}

			if (stored && COLOR_KEYS.every((key) => this.same(stored[key], theme[key]))) return;

			const custom = { ...(stored || {}) };
			COLOR_KEYS.forEach((key) => (custom[key] = theme[key] ? String(theme[key]).toLowerCase() : null));
			await this.apply(custom);
		} catch (error) {
			// el tema que ya está puesto se queda
			console.log("No se pudo refrescar el tema de la aplicación:", error);
		}
	}

	private async apply(custom: any): Promise<void> {
		// Misma base que usa cada modo en el login: la marca guardada (multimarca) o la
		// configuración del ambiente (marca propia).
		const base = environment.APP_MULTIMARCA ? await this.loginMultiModuloService.getLastThemeUsed() : await this.api.getConfig();
		if (!base) return;
		// `false`: `buildFinalTheme` guarda `config_ambiente` con el tema final, que es lo que
		// lee el arranque en frío (`app.component`). Con `true` (marca única) no lo guardaba y
		// la recarga siguiente dependía de que quedara viva una suscripción vieja.
		this.loginMultiModuloService.handleThemeMergeAndApplyFinalTheme(custom, base, false);
	}

	private readStoredCustomConfig(): any {
		try {
			return JSON.parse(localStorage.getItem(CUSTOM_CONFIG_KEY) || "null");
		} catch {
			return null;
		}
	}

	private same(a: unknown, b: unknown): boolean {
		return (a ? String(a).toLowerCase() : null) === (b ? String(b).toLowerCase() : null);
	}
}
